import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as actions from '../../../redux/actions/artistManager';
import * as streamActions from '../../../redux/actions/index';
import { useSelector, useDispatch } from "react-redux";
import StreamIcon from "../../../assets/images/icons/stream.svg";
import FanbaseIcon from "../../../assets/images/icons/fanbase.svg";
import BestTrack from "../../../assets/images/icons/best-track.svg";
import MetaIcon from '../../../assets/images/admin/meta.svg';
import UIcon from "../../../assets/images/icons/u-icon.png";
import FollowersIcon from "../../../assets/images/icons/followers.svg";
import TiktokIcon from "../../../assets/images/admin/Tiktok.png";
import YoutubeIcon from "../../../assets/images/admin/Youtube.png";
import Chart from "react-apexcharts";
import AreaChart from "../charts/AreaChart";
import { useLocation, useNavigate } from "react-router-dom";
import Calendar from "../../../components/Calendar";
import { toast } from "react-toastify";
import { convertDataFormat, getDateRangeFromDuration } from "../../../helper/dataFormat";
import { formatNumber, formatFixedAmount } from "../../../helper/utility";
import ChatInput from "../AmplitudeAI/Chat/ChatInput";
import { PricingPlanModal } from "../../../components/PricingPlanModal";
import { TeamSeatPlanModal } from "../../../components/TeamSeatPlanModal";
import { CreditPlanModal } from "../../../components/CreditPlanModal";
import { showToast } from "../../../helper/ShowToast";
import ArtistManagerMetricCard from "./ArtistManagerMetricCard";
import ArtistManagerDspLockOverlay from "./ArtistManagerDspLockOverlay";
import { useMetaCampaignAnalytics } from "../../../queries/growthEngine/metaCampaign.query";
import { ChartSkeleton } from "../CommonSkeleton";
import { calculateTotalAmount } from "../../../helper/calculation";
import { useTopThreeSmartLinks } from "../../../queries/artistManger/smartLink.query";
import { usePlatformChartsBySource, usePlatformStatsBySource } from "../../../queries/dataAnalyst/platformStats.query";
import AppTour from "../../../components/AppTour/AppTour";
import {
    canUseAmplitudeChat,
    getUserDisplayName,
    hasArtistLinked,
    shouldFetchArtistAnalytics,
} from "../../../helper/access";
import { artistManagerTourSteps } from "../../../components/AppTour/ModuleTourSteps";
import { getUserPurchases } from "../../../redux/actions/billing";

const generateDateRange = (period) => {
    const today = new Date();
    const dates = [];

    try {
        switch (period) {
            case 'weekly':
                for (let i = 6; i >= 0; i--) {
                    const date = new Date(today);
                    date.setDate(date.getDate() - i);
                    dates.push(date.toISOString().split('T')[0]);
                }
                break;

            case 'monthly':
                for (let i = 29; i >= 0; i--) {
                    const date = new Date(today);
                    date.setDate(date.getDate() - i);
                    dates.push(date.toISOString().split('T')[0]);
                }
                break;

            case 'yearly':
                for (let i = 11; i >= 0; i--) {
                    const date = new Date(today);
                    date.setMonth(date.getMonth() - i);
                    dates.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
                }
                break;

            case 'all':
            default:
                for (let i = 11; i >= 0; i--) {
                    const date = new Date(today);
                    date.setMonth(date.getMonth() - i);
                    dates.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
                }
                break;
        }
    } catch (error) {
        console.error('Error generating date range:', error);
        dates.push(today.toISOString().split('T')[0]);
    }

    return dates;
};

function formatTimestamp(ts) {
    const date = new Date(ts);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
}

const platforms = [
    { icon: UIcon, alt: "upncomer", name: "all" },
    // { icon: MetaIcon, alt: "Instagram", name: "instagram" },
    // { icon: TiktokIcon, alt: "Tiktok", name: "tiktok" },
    // { icon: YoutubeIcon, alt: "Youtube", name: "youtube" },
];

const artistHasActiveSeat = (artistProfileId, seatSubscriptions) => {
    if (!artistProfileId) return false;
    const id = String(artistProfileId);
    return (seatSubscriptions || []).some(
        (seat) =>
            seat.status === "active" &&
            seat.artistProfileId &&
            String(seat.artistProfileId) === id
    );
};

export const ArtistManager = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { hash, state: locationState } = location;
    const [runTour, setRunTour] = useState(false)
    const [loading, setLoading] = useState(false);
    const [showPricingPlanModal, setShowPricingPlanModal] = React.useState(false);
    const [showTeamSeatModal, setShowTeamSeatModal] = React.useState(false);
    const [teamSeatArtistProfileId, setTeamSeatArtistProfileId] = React.useState(null);
    const [showCreditPlanModal, setShowCreditPlanModal] = React.useState(false);
    const [selectedDataPeriod, setSelectedDataPeriod] = React.useState("all");
    const [selectedPlatform, setSelectedPlatform] = useState("all")
    const [selectedPerformancePeriod, setSelectedPerformancePeriod] = React.useState("all");
    const [viewMode, setViewMode] = useState("calendar"); // "month" or "year"
    const artistDetails = useSelector(state => state.artistDetails);
    const { artistInfo, artistStats, artistChartStats } = artistDetails;
    const { showLoad } = useSelector((state) => state?.amplitudeReducer);
    const artistManagerReducer = useSelector((state) => state.artistManager);
    const {
        hasActiveSubscription,
        creditDetails,
        purchaseData,
        teamBilling,
        seatSubscriptions,
    } = useSelector((state) => state.billing);
    const access = useSelector((state) => state.auth.access);
    const activeArtistProfileId = access?.artist?.activeArtistProfileId || null;
    const authToken = useSelector((state) => state.auth.token);
    const userDetails = useSelector((state) => state.auth.userDetails);
    const userType = useSelector((state) => state.auth?.userDetails?.userType);
    const hasDspMetricsAccess = useMemo(
        () => shouldFetchArtistAnalytics(access, userDetails) || (!access && hasActiveSubscription),
        [access, userDetails, hasActiveSubscription]
    );
    const needsLinkArtist = !hasArtistLinked(access, userDetails);
    const lockDspMetrics = !hasDspMetricsAccess;
    // Artist link first, then subscription (DSP metric cards only).
    const showDspCardLinkArtistCta = lockDspMetrics && needsLinkArtist;
    const showDspCardSubscribeCta =
        lockDspMetrics && !needsLinkArtist && !hasActiveSubscription;
    const displayName = useMemo(
        () => getUserDisplayName(userDetails, artistInfo, access),
        [userDetails, artistInfo, access]
    );
    const creditsResolved = Boolean(creditDetails?.lastFetched);
    const creditsRemaining = creditDetails?.data?.remaining ?? 0;
    const creditsExhausted = creditsResolved && creditsRemaining <= 0;
    const activeSeatCount = (seatSubscriptions || []).filter((s) => s.status === "active").length;
    const metaAccountKey =
        userDetails?.email || userDetails?.assignedArtistSongstateId || authToken || null;
    const metaCampaignUserKey = metaAccountKey
        ? `${metaAccountKey}:${activeArtistProfileId || "workspace"}`
        : null;

    const {
        data: metaCampaignAnalytics,
        isLoading: isCampaignAnalyticsLoading,
    } = useMetaCampaignAnalytics({
        userKey: metaCampaignUserKey,
        range: selectedPerformancePeriod,
        enabled: !!authToken && !!metaCampaignUserKey,
    });
    const campaignAnalyticLoading = isCampaignAnalyticsLoading && !metaCampaignAnalytics;

    const { data: metaCampaignAnalyticsData } = useMetaCampaignAnalytics({
        userKey: metaCampaignUserKey,
        range: selectedDataPeriod,
        enabled: hasDspMetricsAccess && !!authToken && !!metaCampaignUserKey,
    });

    const { data: platformStatsBySource } = usePlatformStatsBySource({
        userKey: metaCampaignUserKey,
        type: selectedPerformancePeriod,
        source: selectedPlatform,
        enabled: hasDspMetricsAccess && !!authToken && !!metaCampaignUserKey,
    });
    const { data: platformChartStatsBySource } = usePlatformChartsBySource({
        userKey: metaCampaignUserKey,
        type: selectedPerformancePeriod,
        source: selectedPlatform,
        enabled: hasDspMetricsAccess && !!authToken && !!metaCampaignUserKey,
    });
    const events = convertDataFormat(artistManagerReducer?.tasks?.data || [])
    const projectedRevenue = Number(artistManagerReducer?.revenueAnalysis?.data?.metrics?.projectedRevenue || 0);
    const avgMonthlyRevenue = Number(artistManagerReducer?.revenueAnalysis?.data?.metrics?.averageMonthlyRevenue || 0);
    const spotifyStreamsData = artistManagerReducer?.spotifyDataForAnalysis || {}
    const youtubeViewsData = artistManagerReducer?.youtubeDataForAnalysis || {}
    const streamsPlusViews = (spotifyStreamsData?.streamsDelta || 0) + (youtubeViewsData?.streamsDelta || 0);
    const totalPublishingEstimate = (streamsPlusViews * 0.0004) + (streamsPlusViews * 0.00091);
    const totalStreamingEstimate = (spotifyStreamsData?.streamsDelta * 0.0038) + (youtubeViewsData?.streamsDelta * 0.0006);
    const totalEstimatesForRevenue = totalStreamingEstimate + totalPublishingEstimate;
    const extractedPurchaseData = purchaseData?.data?.map(item => ({
        amount: item.amount,
        createdAt: item.createdAt,
        planName: item.plan
    }));
    const totalAmountByPeriod = calculateTotalAmount(extractedPurchaseData, selectedDataPeriod)
    const avgRevenue = artistManagerReducer?.averageRevenue?.data
    const avgExpense = artistManagerReducer?.averageExpense?.data
    const totalExpense = Number(avgExpense?.totalAmount || 0) + totalAmountByPeriod + Number(metaCampaignAnalyticsData?.totals?.spendUsd || 0);
    const totalRevenuesWithEstimates = Number(avgRevenue?.totalAmount || 0) + Number(totalStreamingEstimate) + Number(totalPublishingEstimate)
    const roiPercentage = totalExpense > 0
        ? ((totalRevenuesWithEstimates - totalExpense) / totalExpense) * 100
        : 0;

    const {
        data: topThreeSmartLink = [],
        isLoading: isLinkListLoading,
    } = useTopThreeSmartLinks({ enabled: hasDspMetricsAccess });

    const duration =
        selectedDataPeriod === "all" ? 12 :
            selectedDataPeriod === "yearly" ? 12 :
                selectedDataPeriod === "monthly" ? 1 :
                    1;

    useEffect(() => {
        if(hash === "#start-tour"){
            setRunTour(true)
        }
    }, [hash])

    // Calendar — always available in limited mode
    useEffect(() => {
        dispatch(actions.fetchAllTasks(toast, setLoading));
    }, [dispatch, activeArtistProfileId]);

    // User-logged expenses — available without Pro (total / UpNComer expenditure cards)
    useEffect(() => {
        dispatch(actions.fetchLogExpenseByPeriod(toast, selectedDataPeriod, setLoading));
        dispatch(getUserPurchases({}, "all"));
    }, [dispatch, selectedDataPeriod, activeArtistProfileId]);

    // Songstats / DSP-backed metrics — Pro + artist only
    useEffect(() => {
        if (!hasDspMetricsAccess) return;

        const queryParams = { type: selectedDataPeriod };

        dispatch(streamActions.getArtistInfo(toast));
        dispatch(streamActions.getArtistStats(toast, queryParams, setLoading));
        dispatch(streamActions.getArtistChartStats(toast, queryParams, setLoading));
        dispatch(actions.fetchLogRevenueByPeriod(toast, selectedDataPeriod, setLoading));
        dispatch(actions.getStreamsData(toast, { source: "spotify", duration: selectedDataPeriod }, setLoading, "analysis"));
        dispatch(actions.getStreamsData(toast, { source: "youtube", duration: selectedDataPeriod }, setLoading, "analysis"));
    }, [dispatch, hasDspMetricsAccess, activeArtistProfileId]);

    const navigatToFinancialPlanner = () => {
        navigate('/admin/artist-manager/release-and-financial-planner')
    }

    const navigateToCampaigns = () => {
        navigate('/admin/growth-engine/campaign-history')
    }

    const navigatToLinksAndFanHub = () => {
        navigate('/admin/artist-manager/link-and-fan-hub')
    }


    const navigatToViewBioLink = (link) => {
        if (link?.linkType === "song" || link?.linkType === 'smartlink') {
            navigate(
                `/admin/artist-manager/edit-music-link/${link._id}`,
            );
        } else if (link?.linkType === "biolink") {
            navigate(
                `/admin/artist-manager/edit-bio-link/${link._id}`
            );
        } else if (link?.linkType === "tourlink") {
            navigate(
                `/admin/artist-manager/edit-tour-link/${link._id}`
            );
        }
        else if (link?.linkType === "presave" || link?.featureFmType === "pre_release") {
            navigate(
                `/admin/artist-manager/edit-pre-save/${link._id}`
            );
        }

    }

    const handleDateChange = (value) => { };

    const onPerformancePeriodChange = useCallback(
        (event) => {
            if (selectedPlatform === "youtube" || selectedPlatform === "tiktok") {
                showToast("info", "Work in Progress")
            } else {
                setSelectedPerformancePeriod(event.target.value);
            }
        },
        [dispatch, selectedPerformancePeriod, selectedPlatform]
    );

    const onDataPeriodChange = useCallback(
        (event) => {
            setSelectedDataPeriod(event?.target?.value);
            setLoading(true);
            const queryParams = { type: event?.target?.value };

            const period = event?.target?.value;
            const fetches = [
                dispatch(actions.fetchLogExpenseByPeriod(toast, period, setLoading)),
            ];

            if (hasDspMetricsAccess) {
                fetches.push(
                    dispatch(streamActions.getArtistStats(toast, queryParams, setLoading)),
                    dispatch(streamActions.getArtistChartStats(toast, queryParams, setLoading)),
                    dispatch(actions.fetchLogRevenueByPeriod(toast, period, setLoading)),
                    dispatch(actions.getStreamsData(toast, { source: "spotify", duration: period }, setLoading, "analysis")),
                    dispatch(actions.getStreamsData(toast, { source: "youtube", duration: period }, setLoading, "analysis"))
                );
            }

            Promise.all(fetches)
                .catch((err) => console.error("Error fetching period data:", err))
                .finally(() => setLoading(false));
        },
        [
            dispatch,
            toast,
            setLoading,
            setSelectedDataPeriod,
            streamActions,
            actions,
            totalStreamingEstimate,
            totalPublishingEstimate,
            hasDspMetricsAccess
        ]
    );

    const followersChart = useMemo(() => ({
        name: 'Total Followers',
        categories: platformChartStatsBySource?.totalFollowers?.map(entry => entry.date) || [],
        data: platformChartStatsBySource?.totalFollowers?.map(entry => entry.followers_total) || [],
        intervalType: selectedPerformancePeriod
    }), [platformChartStatsBySource, selectedPerformancePeriod]);

    const streamChart = useMemo(() => ({
        name: 'Total Streams',
        categories: artistChartStats?.totalStreams?.map(entry => entry.date) || [],
        data: artistChartStats?.totalStreams?.map(entry => entry.streams_total) || [],
        intervalType: selectedDataPeriod
    }), [artistChartStats, selectedDataPeriod]);

    // ROI Chart Data
    const roiChart = useMemo(() => {
        const categories = generateDateRange(selectedDataPeriod);

        // Prevent division by zero and Infinity
        let roiPerPeriod = 0;
        if (totalExpense > 0 && Number.isFinite(roiPercentage)) {
            roiPerPeriod = Number((roiPercentage / categories.length).toFixed(2));
        }

        const roiData = categories?.map(() => roiPerPeriod);

        return {
            name: 'ROI %',
            categories: categories,
            data: roiData,
            intervalType: selectedDataPeriod
        };
    }, [roiPercentage, selectedDataPeriod, totalExpense]);

    // Total Expenditure Chart Data
    const totalExpenditureChart = useMemo(() => {
        const categories = generateDateRange(selectedDataPeriod);

        const validExpense = Number.isFinite(totalExpense) ? totalExpense : 0;
        const expensePerPeriod = categories.length > 0
            ? Number((validExpense / categories.length).toFixed(2))
            : 0;
        const expenditureData = categories.map(() => expensePerPeriod);

        return {
            name: 'Total Expenditure',
            categories: categories,
            data: expenditureData,
            intervalType: selectedDataPeriod
        };
    }, [totalExpense, selectedDataPeriod]);

    // UpNComer Expenditure Chart Data
    const upncomerExpenditureChart = useMemo(() => {
        const categories = generateDateRange(selectedDataPeriod);

        const purchases = extractedPurchaseData?.map(item => ({
            amount: item.amount || 0,
            createdAt: item.createdAt,
            planName: item.planId?.name || 'Unknown Plan'
        })) || [];

        const amountByDate = {};

        purchases.forEach(purchase => {
            if (!purchase.createdAt) return;

            const purchaseDate = new Date(purchase.createdAt);
            let key;

            switch (selectedDataPeriod) {
                case 'weekly':
                case 'monthly':
                    key = purchaseDate.toISOString().split('T')[0]; 
                    break;
                case 'yearly':
                case 'all':
                    key = `${purchaseDate.getFullYear()}-${String(purchaseDate.getMonth() + 1).padStart(2, '0')}`;
                    break;
                default:
                    key = purchaseDate.toISOString().split('T')[0];
            }

            amountByDate[key] = (amountByDate[key] || 0) + purchase.amount;
        });

        const upncomerData = categories.map(category => {
            return amountByDate[category] || 0;
        });

        return {
            name: 'UpNComer Expenditure',
            categories: categories,
            data: upncomerData,
            intervalType: selectedDataPeriod
        };
    }, [purchaseData, selectedDataPeriod]);

    // for All time performance section chart- start
    const ExpenditureChart = useMemo(() => ({
        name: 'Total Expenditure',
        categories: metaCampaignAnalytics?.timeline?.map(entry => entry.date) || [],
        data: metaCampaignAnalytics?.timeline?.map(entry => entry.spendUsd) || [],
        intervalType: selectedPerformancePeriod
    }), [metaCampaignAnalytics, selectedPerformancePeriod]);

    const ImpressionsChart = useMemo(() => ({
        name: 'Impression',
        categories: metaCampaignAnalytics?.timeline?.map(entry => entry.date) || [],
        data: metaCampaignAnalytics?.timeline?.map(entry => entry.impressions) || [],
        intervalType: selectedPerformancePeriod
    }), [metaCampaignAnalytics, selectedPerformancePeriod]);

    const CpcChart = useMemo(() => ({
        name: 'Cost-Per-Fan',
        categories: metaCampaignAnalytics?.timeline?.map(entry => entry.date) || [],
        data: metaCampaignAnalytics?.timeline?.map(entry => entry.cpc) || [],
        intervalType: selectedPerformancePeriod
    }), [metaCampaignAnalytics, selectedPerformancePeriod]);
    // for All time performance section chart- End
    const onSocialPlatformChange = useCallback(
        (value) => {
            if (value === 'youtube' || value === 'tiktok') {
                showToast("info", "Work in Progress")
            } else {
                setSelectedPlatform(value);
            }
        },
        []
    );

    const getMonthName = (monthIndex) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return months[monthIndex] || '';
    };
    function pad2(n) { return String(n).padStart(2, '0'); }
    function formatDDMMM(date) {
        return `${pad2(date.getDate())} ${getMonthName(date.getMonth())}`;
    }
    function formatMonYYYY(date) {
        return `${getMonthName(date.getMonth())} ${date.getFullYear()}`;
    }

    // determine number of points and label granularity based on selectedDataPeriod
    const totalPoints = useMemo(() => {
        if (selectedDataPeriod === 'weekly') return 7;
        if (selectedDataPeriod === 'monthly') return 30;
        if (selectedDataPeriod === 'yearly') return 12;
        if (selectedDataPeriod === 'all') return 12; // show last 12 months for "all"
        const d = Number(duration || 0);
        return (isNaN(d) || d <= 0) ? 1 : d + 1;
    }, [selectedDataPeriod, duration]);

    const monthLabels = useMemo(() => {
        const now = new Date();
        if (selectedDataPeriod === 'weekly' || selectedDataPeriod === 'monthly') {
            return Array.from({ length: totalPoints }, (_, i) => {
                const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (totalPoints - 1 - i));
                return formatDDMMM(d);
            });
        }
        if (selectedDataPeriod === 'yearly' || selectedDataPeriod === 'all') {
            return Array.from({ length: totalPoints }, (_, i) => {
                const monthsBack = totalPoints - 1 - i;
                const d = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
                return formatMonYYYY(d);
            });
        }
        return Array.from({ length: totalPoints }, (_, i) => {
            const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
            return `${getMonthName(d.getMonth())} ${d.getFullYear()}`;
        });
    }, [selectedDataPeriod, totalPoints, duration]);

    const avgMonthlyRevenueData = useMemo(() => {
        const len = Math.max(0, totalPoints);
        const revenueValue = totalRevenuesWithEstimates || 0;

        const val = Number.isFinite(revenueValue)
            ? Number(revenueValue.toFixed(2))
            : 0;

        return Array.from({ length: len }, () => val);
    }, [totalRevenuesWithEstimates, totalPoints]);

    const chartOptions = useMemo(() => {
        return {
            chart: {
                type: 'area',
                height: 200,
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: true, easing: 'easeinout', speed: 800 }
            },
            stroke: { curve: 'smooth', show: true, width: 2, colors: ['#00067E'] },
            xaxis: {
                categories: monthLabels,
                labels: {
                    show: false,
                    style: { fontSize: '10px', fontWeight: 500, colors: '#1F2937' },
                    rotate: -45,
                    rotateAlways: monthLabels.length > 6
                },
                axisBorder: { show: true, color: '#E5E7EB' },
                tooltip: { enabled: false }
            },
            yaxis: {
                labels: {
                    show: false,
                    style: { fontSize: '10px', fontWeight: 500, colors: '#1F2937' },
                    formatter: (value) => `$${formatNumber(value)}`
                },
                min: 0
            },
            legend: { show: false },
            grid: {
                xaxis: { lines: { show: false } },
                yaxis: { lines: { show: false } },
                padding: { top: 0, right: 5, bottom: 0, left: 5 }
            },
            tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
                y: { formatter: (value) => `$${formatNumber(value)}` },
                style: { fontSize: '12px' }
            },
            dataLabels: { enabled: false },
            fill: { opacity: 1 }
        };
    }, [monthLabels, selectedDataPeriod]);

    const chartSeries = useMemo(() => {
        return [
            { name: 'Revenue Generated', data: avgMonthlyRevenueData }
        ];
    }, [avgMonthlyRevenueData]);

    const chartKey = `${selectedDataPeriod || duration}-${totalRevenuesWithEstimates}-${monthLabels.join('|')}`;

    const handleSend = (input) => {
        if (access && !canUseAmplitudeChat(access)) {
            showToast("info", "Amplitude AI chat is not available for your account.");
            return;
        }
        if (creditsExhausted) {
            setShowCreditPlanModal(true);
            return;
        }
        dispatch(streamActions.sendMessage(toast, input));
        navigate("/admin/amplitude-ai");
    };

    const handleLinkArtistClick = () => {
        showToast("info", "Link your artist profile to unlock artist insights.");
        navigate("/link-accounts");
    };

    const handleProClick = async () => {
        try {
            setLoading(true); 
            if (hasActiveSubscription) {
                await dispatch(streamActions.getCustomerPortalSession());
            } else {
                setShowPricingPlanModal(true);
            }
        } catch (err) {
            console.error('Error handling Pro button:', err);
        } finally {
            setLoading(false);
        }
    };

    /** Open add-on checkout for an artist (call after create artist or via navigation state). */
    const handleSubscribeTeamSeat = (artistProfileId) => {
        if (!artistProfileId) {
            showToast("info", "Create an artist profile first, then subscribe a team seat.");
            return;
        }
        if (!hasActiveSubscription || !teamBilling?.hasMainSubscription) {
            setShowPricingPlanModal(true);
            return;
        }
        if (artistHasActiveSeat(artistProfileId, seatSubscriptions)) {
            showToast("info", "This artist already has an active seat.");
            return;
        }
        setTeamSeatArtistProfileId(artistProfileId);
        setShowTeamSeatModal(true);
    };

    const closeTeamSeatModal = () => {
        setShowTeamSeatModal(false);
        setTeamSeatArtistProfileId(null);
    };

    // e.g. navigate('/admin/artist-manager', { state: { artistProfileId: '...' } })
    useEffect(() => {
        const artistId =
            locationState?.artistProfileId ||
            locationState?.requireTeamSeatForArtistId;
        if (!artistId) return;
        handleSubscribeTeamSeat(artistId);
        navigate(location.pathname + location.hash, { replace: true, state: {} });
    }, [locationState?.artistProfileId, locationState?.requireTeamSeatForArtistId]);

    const totalRevenuesGenerated = formatNumber(totalRevenuesWithEstimates)

    return (
        <div className="ArtistManager-Page py-4">
            <AppTour
                tourSteps={artistManagerTourSteps}
                runTour={runTour}
                setRunTour={setRunTour}
            />
            <div className="container max-w-full">
                <div className="card bg-gradient-to-r from-theme_clr1 to-theme_clr2 rounded-xl mb-4 2xl:mb-5 p-4 lg:p-5">
                    <h5 className="text-white text-[23px] font-manrope font-bold leading-tight mb-[11px]">Your Manager</h5>
                    <div className="text-white text-xl font-jakarta">Recording and optimising your operations - just without the cut.</div>
                {   /*{(userType === "organization" || activeSeatCount > 0) && (
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            <span className="text-white/90 text-sm">
                                Team seats: {activeSeatCount} active
                                {teamBilling?.mainSubscriptionInterval
                                    ? ` · Main plan: ${teamBilling.mainSubscriptionInterval}`
                                    : ""}
                            </span>
                            {teamBilling?.hasMainSubscription && userType === "organization" && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        handleSubscribeTeamSeat(
                                            teamSeatArtistProfileId ||
                                                locationState?.artistProfileId ||
                                                locationState?.requireTeamSeatForArtistId
                                        )
                                    }
                                    className="text-sm font-semibold px-3 py-1.5 rounded-lg bg-[#FA6138] text-white hover:bg-[#e55532]"
                                >
                                    Add team artist seat
                                </button>
                            )}
                        </div>
                    )}
                } */}
                </div>

                {/* Manage Campaign Section */}
                <div className="performance-section bg-theme_clr1 rounded-xl mb-4 2xl:mb-5 p-4 xl:p-6" data-tour="artist-performance">
                    <div className="header-section flex flex-wrap items-center justify-between gap-2 mb-5">
                        <div className="flex flex-wrap gap-x-6 gap-y-3">
                            <select onChange={onPerformancePeriodChange} className="appearance-none bg-theme_clr1 text-white text-base font-manrope border-0 focus:outline-none pr-8" style={{ WebkitAppearance: "none", MozAppearance: "none", appearance: "none" }}>
                                <option value="all">All time performance</option>
                                <option value="weekly">Weekly performance</option>
                                <option value="monthly">Monthly performance</option>
                                <option value="yearly">Yearly performance</option>
                            </select>
                            <div className="icons flex items-center gap-6">
                                {platforms.map((platform) => (
                                    <img
                                        key={platform.alt}
                                        src={platform.icon}
                                        alt={platform.alt}
                                        onClick={() => onSocialPlatformChange(platform.name)}
                                        className={`block w-[30px] h-[30px] cursor-pointer ${platform.name === "youtube" || platform.name === "tiktok" ? "opacity-50" : ''} ${selectedPlatform === platform.name
                                            ? "ring-4 ring-orange-500 squared-full rounded"
                                            : ""
                                            }`}
                                    />
                                ))}
                            </div>

                        </div>
                        <button type="button" onClick={navigateToCampaigns} className="btn-custom3">Manage Campaigns</button>
                    </div>
                    <div className="relative">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 2xl:gap-[22px] flex-nowrap overflow-auto">
                        <div className="grid-item">
                            <ArtistManagerMetricCard
                                locked={lockDspMetrics}
                                showLockCta={showDspCardSubscribeCta}
                                showLinkArtistCta={showDspCardLinkArtistCta}
                                onSubscribe={handleProClick}
                                onLinkArtist={handleLinkArtistClick}
                                lockIcon={FollowersIcon}
                                lockLabel="Followers"
                                lockPreviewValue="132k"
                                lockChartType="area"
                            >
                                <div className="icon mb-1">
                                    <img src={FollowersIcon} alt="FollowersIcon" />
                                </div>
                                <div className="chart-wrapper" style={{ marginLeft: "-10px" }}>
                                    {followersChart?.categories?.length > 0 && followersChart?.data?.length > 0 ? (
                                        <AreaChart key={selectedPerformancePeriod} state={followersChart} />
                                    ) : (
                                        <div className="flex items-center justify-center text-sm text-gray-500" style={{ height: "120px" }}>
                                            No data available
                                        </div>
                                    )}
                                </div>
                                <div className="cover">
                                    <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">{formatNumber(platformStatsBySource?.totalFollowers)}</div>
                                    <div className="text-theme_clr1 text-[12.8px]">Followers</div>
                                </div>
                            </ArtistManagerMetricCard>
                        </div>
                        <div className="grid-item">
                            <ArtistManagerMetricCard
                                locked={false}
                                lockIcon={FanbaseIcon}
                                lockLabel="Cost-Per-Fan"
                                lockPreviewValue="$0.28"
                                lockChartType="area"
                            >
                                <div className="icon mb-1">
                                    <img src={FanbaseIcon} alt="Cost-Per-Fan" />
                                </div>
                                <div className="chart-wrapper" style={{ marginLeft: "-10px" }}>
                                    {campaignAnalyticLoading ? (
                                        <ChartSkeleton />
                                    ) : metaCampaignAnalytics && metaCampaignAnalytics?.timeline?.length > 0 ? (
                                        <AreaChart key={selectedPerformancePeriod} state={CpcChart} />
                                    ) : (
                                        <div className="flex items-center justify-center text-sm font-bold text-gray-500" style={{ height: "120px" }}>
                                            No CPC chart available
                                        </div>
                                    )}
                                </div>
                                <div className="cover">
                                    {campaignAnalyticLoading ? (
                                        <div className="h-4 w-12 bg-gray-200 rounded animate-pulse mb-1" />
                                    ) : (
                                        <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">
                                            {metaCampaignAnalytics?.totals?.currency
                                                ? `${metaCampaignAnalytics.totals.currency} ${metaCampaignAnalytics?.totals?.cpc || 0}`
                                                : (metaCampaignAnalytics?.totals?.cpc || 0)}
                                        </div>
                                    )}
                                    <div className="text-theme_clr1 text-[12.8px]">Cost-Per-Fan</div>
                                </div>
                            </ArtistManagerMetricCard>
                        </div>
                        <div className="grid-item">
                            <ArtistManagerMetricCard
                                locked={lockDspMetrics}
                                showLockCta={showDspCardSubscribeCta}
                                showLinkArtistCta={showDspCardLinkArtistCta}
                                onSubscribe={handleProClick}
                                onLinkArtist={handleLinkArtistClick}
                                lockIcon={BestTrack}
                                lockLabel="Best Performing Song"
                                lockPreviewValue="143k"
                                lockChartType="image"
                            >
                                <div className="icon">
                                    <img src={BestTrack} alt="Best Track" />
                                    <div className="text-black text-[12.26px] font-bold mt-2">{platformStatsBySource?.topTrack?.track_name}</div>
                                </div>
                                <div className="chart-wrapper">
                                    <img src={platformStatsBySource?.topTrack?.image_url} alt="Best Track" className="block w-[400px] mx-auto" />
                                </div>
                                <div className="cover">
                                    <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">{formatNumber(platformStatsBySource?.topTrack?.streamsCount)} Views</div>
                                    <div className="text-theme_clr1 text-[12.8px]">Best Performing Song</div>
                                </div>
                            </ArtistManagerMetricCard>
                        </div>
                        <div className="grid-item">
                            <ArtistManagerMetricCard
                                locked={false}
                                lockIcon={<i className="bi bi-currency-dollar text-4xl leading-none"></i>}
                                lockLabel="Ad Expenditure"
                                lockPreviewValue="$1.2k"
                                lockChartType="area"
                            >
                                <div className="icon mb-1">
                                    <i className="bi bi-currency-dollar text-4xl leading-none"></i>
                                </div>
                                {campaignAnalyticLoading ? (
                                    <ChartSkeleton />
                                ) : metaCampaignAnalytics && metaCampaignAnalytics?.timeline?.length > 0 ? (
                                    <AreaChart key={selectedPerformancePeriod} state={ExpenditureChart} />
                                ) : (
                                    <div className="flex items-center justify-center text-sm font-bold text-gray-500" style={{ height: "120px" }}>
                                        No Expenditure chart available
                                    </div>
                                )}
                                <div className="cover">
                                    {campaignAnalyticLoading ? (
                                        <div className="h-4 w-12 bg-gray-200 rounded animate-pulse mb-1" />
                                    ) : (
                                        <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">
                                            ${formatFixedAmount(metaCampaignAnalytics?.totals?.spendUsd) ?? "0.00"}
                                            {Number(formatFixedAmount(metaCampaignAnalytics?.totals?.spend)) > 0 && metaCampaignAnalytics?.totals?.currency
                                                ? ` (${metaCampaignAnalytics.totals.currency} ${formatFixedAmount(metaCampaignAnalytics.totals.spend)})`
                                                : ""}
                                        </div>
                                    )}
                                    <div className="text-theme_clr1 text-[12.8px]">Ad Expenditure</div>
                                </div>
                            </ArtistManagerMetricCard>
                        </div>
                        <div className="grid-item">
                            <ArtistManagerMetricCard
                                locked={false}
                                lockIcon={StreamIcon}
                                lockLabel="Impressions"
                                lockPreviewValue="45k"
                                lockChartType="area"
                            >
                                <div className="icon mb-1">
                                    <img src={StreamIcon} alt="Streams" />
                                </div>
                                {campaignAnalyticLoading ? (
                                    <ChartSkeleton />
                                ) : metaCampaignAnalytics && metaCampaignAnalytics?.timeline?.length > 0 ? (
                                    <AreaChart key={selectedPerformancePeriod} state={ImpressionsChart} />
                                ) : (
                                    <div className="flex items-center justify-center text-sm font-bold text-gray-500" style={{ height: "120px" }}>
                                        No Impressions chart available
                                    </div>
                                )}
                                <div className="cover">
                                    {campaignAnalyticLoading ? (
                                        <div className="h-4 w-12 bg-gray-200 rounded animate-pulse mb-1" />
                                    ) : (
                                        <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">{formatNumber(metaCampaignAnalytics?.totals?.impressions) || 0}</div>
                                    )}
                                    <div className="text-theme_clr1 text-[12.8px]">Impressions</div>
                                </div>
                            </ArtistManagerMetricCard>
                        </div>
                    </div>
                    </div>
                </div>

                {/* Manage Finances Section */}
                <div className="performance-section bg-theme_clr1 rounded-xl mb-4 2xl:mb-5 p-4 xl:p-6" data-tour="financial-performance">
                    <div className="header-section flex flex-wrap items-center justify-between gap-2 mb-5">
                        <div className="flex flex-wrap gap-6">
                            <select onChange={onDataPeriodChange} className="appearance-none bg-theme_clr1 text-white text-white text-base font-manrope border-0 focus:outline-none pr-8" style={{ WebkitAppearance: "none", MozAppearance: "none", appearance: "none" }}>
                                <option value="all">All time data</option>
                                <option value="weekly">Weekly data</option>
                                <option value="monthly">Monthly data</option>
                                <option value="yearly">Yearly data</option>
                            </select>
                        </div>
                        <button type="button" onClick={navigatToFinancialPlanner} className="btn-custom3">Manage Finances <i className="bi bi-plus-circle text-base ms-1"></i></button>
                    </div>
                    <div className="relative">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 2xl:gap-[22px] flex-nowrap overflow-auto">
                        <div className="grid-item">
                            <ArtistManagerMetricCard
                                locked={lockDspMetrics}
                                showLockCta={showDspCardSubscribeCta}
                                showLinkArtistCta={showDspCardLinkArtistCta}
                                onSubscribe={handleProClick}
                                onLinkArtist={handleLinkArtistClick}
                                lockIcon={FollowersIcon}
                                lockLabel="Revenue Generated"
                                lockPreviewValue="$434.9k"
                                lockChartType="area"
                                lockChartHeight={200}
                            >
                                <div className="icon mb-1">
                                    <img src={FollowersIcon} alt="FollowersIcon" />
                                </div>
                                <div className="chart-wrapper" style={{ marginLeft: "-10px" }}>
                                    <Chart key={chartKey} options={chartOptions} series={chartSeries} type="area" height={200} />
                                </div>
                                <div className="cover">
                                    <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">
                                        ${typeof totalRevenuesGenerated === "string"
                                            ? totalRevenuesGenerated
                                            : (Number.isFinite(totalRevenuesGenerated) ? Number(totalRevenuesGenerated).toFixed(2) : "0.00")}
                                    </div>
                                    <div className="text-theme_clr1 text-[12.8px]">Revenue Generated</div>
                                </div>
                            </ArtistManagerMetricCard>
                        </div>
                        <div className="grid-item">
                            <ArtistManagerMetricCard
                                locked={lockDspMetrics}
                                showLockCta={showDspCardSubscribeCta}
                                showLinkArtistCta={showDspCardLinkArtistCta}
                                onSubscribe={handleProClick}
                                onLinkArtist={handleLinkArtistClick}
                                lockIcon={BestTrack}
                                lockLabel="ROI %"
                                lockPreviewValue="43%"
                                lockChartType="area"
                            >
                                <div className="icon mb-1">
                                    <img src={BestTrack} alt="Best Track" />
                                </div>
                                <div className="chart-wrapper" style={{ marginLeft: "-10px" }}>
                                    {roiChart.categories.length > 0 ? (
                                        <AreaChart key={selectedDataPeriod} state={roiChart} />
                                    ) : (
                                        <div className="flex items-center justify-center text-sm text-gray-500" style={{ height: "120px" }}>
                                            No data available
                                        </div>
                                    )}
                                </div>
                                <div className="cover">
                                    <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">
                                        {Number.isFinite(roiPercentage) ? roiPercentage.toFixed(0) : 0}%
                                    </div>
                                    <div className="text-theme_clr1 text-[12.8px]">ROI %</div>
                                </div>
                            </ArtistManagerMetricCard>
                        </div>
                        <div className="grid-item">
                            <ArtistManagerMetricCard
                                locked={false}
                                lockIcon={<i className="bi bi-currency-dollar text-4xl leading-none"></i>}
                                lockLabel="Total Expenditure"
                                lockPreviewValue="$100"
                                lockChartType="area"
                            >
                                <div className="icon mb-1">
                                    <i className="bi bi-currency-dollar text-4xl leading-none"></i>
                                </div>
                                <div className="chart-wrapper" style={{ marginLeft: "-10px" }}>
                                    {totalExpenditureChart.categories.length > 0 ? (
                                        <AreaChart key={selectedDataPeriod} state={totalExpenditureChart} />
                                    ) : (
                                        <div className="flex items-center justify-center text-sm text-gray-500" style={{ height: "120px" }}>
                                            No data available
                                        </div>
                                    )}
                                </div>
                                <div className="cover">
                                    <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">${formatNumber(totalExpense)}</div>
                                    <div className="text-theme_clr1 text-[12.8px]">Total Expenditure</div>
                                </div>
                            </ArtistManagerMetricCard>
                        </div>
                        <div className="grid-item">
                            <ArtistManagerMetricCard
                                locked={false}
                                lockIcon={FanbaseIcon}
                                lockLabel="UpNComer Expenditure"
                                lockPreviewValue="$0.28"
                                lockChartType="area"
                            >
                                <div className="icon mb-1">
                                    <img src={FanbaseIcon} alt="Fanbase" />
                                </div>
                                <div className="chart-wrapper" style={{ marginLeft: "-10px" }}>
                                    {upncomerExpenditureChart.categories.length > 0 ? (
                                        <AreaChart key={selectedDataPeriod} state={upncomerExpenditureChart} />
                                    ) : (
                                        <div className="flex items-center justify-center text-sm text-gray-500" style={{ height: "120px" }}>
                                            No data available
                                        </div>
                                    )}
                                </div>
                                <div className="cover">
                                    <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">${Number(totalAmountByPeriod)}</div>
                                    <div className="text-theme_clr1 text-[12.8px]">UpNComer Expenditure</div>
                                </div>
                            </ArtistManagerMetricCard>
                        </div>
                        <div className="grid-item">
                            <ArtistManagerMetricCard
                                locked={lockDspMetrics}
                                showLockCta={showDspCardSubscribeCta}
                                showLinkArtistCta={showDspCardLinkArtistCta}
                                onSubscribe={handleProClick}
                                onLinkArtist={handleLinkArtistClick}
                                lockIcon={StreamIcon}
                                lockLabel="Streams + Views"
                                lockPreviewValue="3.2M"
                                lockChartType="area"
                            >
                                <div className="icon mb-1">
                                    <img src={StreamIcon} alt="Streams" />
                                </div>
                                <div className="chart-wrapper" style={{ marginLeft: "-10px" }}>
                                    {streamChart.categories.length > 0 ? (
                                        <AreaChart key={selectedDataPeriod} state={streamChart} />
                                    ) : (
                                        <div className="flex items-center justify-center text-sm text-gray-500" style={{ height: "120px" }}>
                                            No data available
                                        </div>
                                    )}
                                </div>
                                <div className="cover">
                                    <div className="text-theme_clr1 text-[12.26px] font-bold mb-[2px]">{formatNumber(artistStats?.totalStreams)}</div>
                                    <div className="text-theme_clr1 text-[12.8px]">Streams + Views</div>
                                </div>
                            </ArtistManagerMetricCard>
                        </div>
                    </div>
                    </div>
                </div>

                {/* Input Section */}
                <div className="mb-4" data-tour="amplitude-ai-chat">
                    <ChatInput
                        title={`Launch campaigns to grow your fanbase, ${displayName}.`}
                        inputPlaceholder="Use Amplitude in crafting the perfect campaign based on your vision."
                        isDisabled={showLoad}
                        creditsExhausted={creditsExhausted}
                        onUpgradeClick={() => setShowPricingPlanModal(true)}
                        onGetCreditsClick={() => setShowCreditPlanModal(true)}
                        onSend={handleSend}
                    />
                </div>
                {/* <div className="card bg-gradient-to-r from-theme_clr1 to-theme_clr2 rounded-xl mb-4 2xl:mb-5 p-4 lg:p-5">
                    <h5 className='text-white text-[23px] font-manrope font-bold leading-tight mb-4'></h5>
                    <div className="input-wrapper flex flex-wrap gap-[11px]">
                        <input type="text" className='flex-1 bg-transparent text-white placeholder:text-white text-base font-jakarta font-medium border border-white rounded-full px-5 py-3' placeholder='Use Amplitude in crafting the perfect campaign based on your vision.' required />
                        <button className='btn-custom2 w-[192px] flex items-center justify-center gap-5 shadow-lg'>Submit <img src={ButtonIcon} alt="Button Icon" /></button>
                    </div>
                </div> */}

                {/* Grid Listing Section */}
                <div className="performance-section">
                    <div className="grid lg:grid-cols-2 gap-4">
                        <div className="grid-item" data-tour="release-planner">
                            <div className="info-box h-full bg-theme_clr1 flex flex-col justify-between rounded-xl p-4 xl:p-5">
                                <div className="header-section flex flex-wrap items-center justify-between gap-2 mb-[10px] md:mb-[21px]">
                                    <select
                                        value={viewMode}
                                        onChange={(e) => setViewMode(e.target.value)}
                                        className="appearance-none bg-theme_clr1 text-white text-white text-base font-manrope border-0 focus:outline-none pr-8" style={{ WebkitAppearance: "none", MozAppearance: "none", appearance: "none" }}>
                                        <option value="month">Month</option>
                                        <option value="year">Year</option>
                                    </select>
                                    <button type="button" onClick={navigatToFinancialPlanner} className="btn-custom3">Plan your Schedule  <i className="bi bi-plus-circle text-base font-bold ms-1"></i></button>
                                </div>
                                <Calendar mode={viewMode} eventsData={events} onChange={handleDateChange} />
                            </div>
                        </div>
                        <div className="grid-item" data-tour="links-and-fan-hub">
                            <div className="info-box h-full bg-theme_clr1 flex flex-col justify-between rounded-xl p-4 xl:p-5">
                                <div className="header-section flex flex-wrap items-center justify-between gap-2 text-white mb-[10px] md:mb-[21px]">
                                    <div className="title-info text-[23px] font-bold font-manrope flex items-center gap-2 leading-tight">
                                        <img src={require('../../../assets/images/admin/Amplitude-Info.png')} alt="Amplitude Info" className='w-[30px] h-[30px]' /> Links and Fan Hub
                                    </div>
                                    <button type="button" onClick={navigatToLinksAndFanHub} className="btn-custom3">Manage Links and Fans <i className="bi bi-plus-circle text-base ms-1"></i></button>
                                </div>

                                <div className="relative flex min-h-[220px] flex-1 flex-col overflow-hidden rounded-xl bg-white p-4">
                                    {lockDspMetrics && (
                                        <>
                                            <div className="relative z-0 flex flex-1 flex-col">
                                                {[
                                                    { title: "Bio Link", stats: "Conversions: 1.2k · Created: Jan 12" },
                                                    { title: "Music Link", stats: "Conversions: 890 · Created: Feb 3" },
                                                    { title: "Pre-Save Link", stats: "Conversions: 456 · Created: Mar 8" },
                                                ].map((row, i) => (
                                                    <div
                                                        key={row.title}
                                                        className={`flex flex-row flex-wrap items-center gap-4 ${i !== 2 ? "border-b border-theme_clr2 pb-3 mb-3" : ""}`}
                                                    >
                                                        <div className="flex h-[80px] w-[80px] items-center justify-center rounded-xl bg-gray-100">
                                                            <i className="bi bi-link-45deg text-2xl text-gray-400" aria-hidden />
                                                        </div>
                                                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                                                            <div className="text-[18px] font-medium text-theme_clr1">
                                                                {row.title}
                                                            </div>
                                                            <div className="text-[12.8px] text-gray-500 opacity-80">
                                                                {row.stats}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            {needsLinkArtist ? (
                                                <ArtistManagerDspLockOverlay
                                                    needsLinkArtist
                                                    onLinkArtist={handleLinkArtistClick}
                                                    onSubscribe={handleProClick}
                                                    title="Unlock Links & Fan Hub"
                                                    description="Link your artist profile to preview link performance and manage your fan hub analytics."
                                                    buttonLabel="Link Your Artist"
                                                />
                                            ) : (
                                                <ArtistManagerDspLockOverlay
                                                    needsLinkArtist={false}
                                                    onLinkArtist={handleLinkArtistClick}
                                                    onSubscribe={handleProClick}
                                                    title="Unlock Links & Fan Hub"
                                                    description="Subscribe to unlock link performance data and fan hub analytics."
                                                />
                                            )}
                                        </>
                                    )}
                                    {hasDspMetricsAccess && isLinkListLoading ? (
                                        [...Array(3)].map((_, i) => (
                                            <div
                                                key={i}
                                                className={`flex flex-row flex-wrap items-center gap-4 flex-1 animate-pulse ${i !== 2 ? "border-b border-theme_clr2 pb-3 mb-3" : ""
                                                    }`}
                                            >
                                                {/* Left Image Skeleton */}
                                                <div className="w-[80px] h-[80px] bg-gray-200 rounded-xl"></div>

                                                {/* Right Text Skeleton */}
                                                <div className="flex flex-col flex-1 gap-2">
                                                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                                                    <div className="h-3 bg-gray-200 rounded w-1/4"></div>

                                                    <div className="flex gap-3">
                                                        <div className="h-3 bg-gray-200 rounded w-1/5"></div>
                                                        <div className="h-3 bg-gray-200 rounded w-1/5"></div>
                                                        <div className="h-3 bg-gray-200 rounded w-1/5"></div>
                                                    </div>
                                                </div>

                                                <div className="h-6 w-16 bg-gray-200 rounded"></div>
                                            </div>
                                        ))
                                    ) : hasDspMetricsAccess && topThreeSmartLink?.length === 0 ? (
                                        <div className="text-center text-gray-500 py-6 text-sm">
                                            No smart link available
                                        </div>

                                    ) : hasDspMetricsAccess ? (
                                        topThreeSmartLink?.map((link, index) => (
                                            <div
                                                key={link?._id}
                                                className={`item-info flex flex-row flex-wrap items-center gap-4 flex-1 ${index !== topThreeSmartLink?.length - 1
                                                    ? "border-b border-theme_clr2 pb-3 mb-3"
                                                    : ""
                                                    }`}
                                            >
                                                <img
                                                    src={link.image}
                                                    alt="Campaign"
                                                    className="w-[80px] h-[80px] rounded-xl object-cover"
                                                />

                                                {/* RIGHT CONTENT */}
                                                <div className="info-wrapper flex flex-row flex-wrap items-center justify-between flex-1 gap-4">
                                                    <div className="content-wrapper text-theme_clr1">
                                                        <div className="font-medium text-[18px] mb-[2px]">
                                                            {link.title}
                                                        </div>

                                                        <div className="font-inter text-[12.8px]">
                                                            <div className="mb-1">
                                                                Artist: {link?.artistName || artistInfo?.name}
                                                            </div>

                                                            <div className="flex flex-wrap items-center gap-x-2">
                                                                <span>Conversions: {link?.clicksToService}</span>
                                                                <span className="h-[5px] w-[5px] bg-theme_clr1 inline-block rounded-full"></span>

                                                                <span>
                                                                    Created on: {formatTimestamp(link.createdAt)}
                                                                    <a
                                                                        href={link?.fullLink}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        <i className="bi bi-box-arrow-up-right text-theme_clr4 cursor-pointer ms-1"></i>
                                                                    </a>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* VIEW BUTTON */}
                                                    <div className="btn-wrapper flex items-center">
                                                        <button
                                                            onClick={() => navigatToViewBioLink(link)}
                                                            className="text-theme_clr4 font-bold flex items-center gap-2 transition-transform duration-100 active:scale-95 active:shadow-inner"
                                                        >
                                                            View <i className="bi bi-chevron-right"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : null}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

            </div >
            <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/admin/artist-manager"} />
            <TeamSeatPlanModal
                isOpen={showTeamSeatModal}
                onClose={closeTeamSeatModal}
                returnUrl={window.location.origin + "/admin/artist-manager"}
                artistProfileId={teamSeatArtistProfileId}
            />
            <CreditPlanModal isOpen={showCreditPlanModal} onClose={() => setShowCreditPlanModal(false)} />
        </div >
    )
}
